import React, { useState, useEffect } from 'react';
import { gql } from 'graphql-tag';
import {
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CardHeader,
  Box,
  TextField,
  Typography,
  Divider,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import MessageIcon from '@mui/icons-material/Message';
import PurpleButton from '../common/buttonTheme';
import PageTitle from '../common/page_title';
import { useMutation, useQuery } from '@apollo/client';
import { GET_TODOS_AND_CATEGORIES, UPDATE_CONTACT_METADATA } from '../apis/graphqlQueries';
import ConversationView from '../toDoList/ConversationView';

const SEND_CONTACT_MUTATION = gql`
  mutation sendContactForm($input: ContactFormInput!) {
    sendContactForm(input: $input) {
      success
      error
    }
  }
`;

const FormInput = ({ label, error, ...props }) => (
  <TextField
    fullWidth
    label={label}
    error={!!error}
    helperText={error}
    margin="normal"
    {...props}
  />
);

const StatusMessage = ({ status }) => {
  if (!status) return null;

  return (
    <Alert severity={status.type === 'success' ? 'success' : 'error'}>
      <AlertTitle>{status.type === 'success' ? 'Success' : 'Error'}</AlertTitle>
      {status.message}
    </Alert>
  );
};

const ContactForm = ({ showResolved = false, ...user }) => {
  const [sendForm] = useMutation(SEND_CONTACT_MUTATION);
  const [updateMetadata] = useMutation(UPDATE_CONTACT_METADATA);
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [hashedUserId, setHashedUserId] = useState(null);
  const user_id = user.sub.split("|")[1];

  // Hash user ID for comparison with conversation author IDs
  useEffect(() => {
    const hashUserId = async (userId) => {
      const encoder = new TextEncoder();
      const data = encoder.encode(userId);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    };

    if (user_id) {
      hashUserId(user_id).then(setHashedUserId);
    }
  }, [user_id]);

  // Fetch todos to get contact submissions
  const { data, loading, refetch } = useQuery(GET_TODOS_AND_CATEGORIES, {
    variables: { user_id },
  });

  const executeGraphQL = async (formInput) => {
    const contactInput = {
      input: {
        message: formInput.message,
        name: user.name,
        email: user.email,
        user_id: user_id
      }
    };
    
    const { data } = await sendForm({ 
      variables: contactInput
    });
    
    return data.sendContactForm;
  };

  const validateForm = (values) => {
    const errors = {};
    if (!values.message) errors.message = 'Message is required';
    return errors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);
    setErrors({});

    const values = Object.fromEntries(new FormData(e.target).entries());
    const validationErrors = validateForm(values);

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await executeGraphQL(values);
      setStatus({
        type: result.success ? 'success' : 'error',
        message: result.success ? 'Message sent successfully!' : result.error,
      });
      if (result.success) {
        e.target.reset();
        // Refetch to show the new contact submission
        refetch();
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to send message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter contact todos - using isAdminContactTodo flag to identify contact form submissions
  // Backend already returns todos where user is owner OR responder
  // Apply resolved/pending filter, search filter, and sort by submission date (most recent first)
  const contactTodos = data?.getTodosAndCategories?.todos
    ?.filter(todo =>
      todo.isAdminContactTodo === true
    )
    ?.filter(todo => {
      // Filter by resolved status
      const isResolved = todo.contactMetadata?.customerResolved === true;
      return showResolved ? isResolved : !isResolved;
    })
    ?.filter(todo => {
      // If no search string, show all
      if (!searchString || searchString.length < 2) return true;

      const search = searchString.toLowerCase();

      // Search in description/original message
      if (todo.description?.toLowerCase().includes(search)) return true;

      // Search in conversation messages
      if (todo.conversation?.some(msg => msg.message?.toLowerCase().includes(search))) return true;

      return false;
    })
    ?.sort((a, b) => {
      const dateA = new Date(a.contactMetadata?.submittedDate || 0);
      const dateB = new Date(b.contactMetadata?.submittedDate || 0);
      return dateB - dateA; // Most recent first
    }) || [];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  const handleConversationUpdate = () => {
    refetch();
  };

  // Format timestamp helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Mark item as resolved
  const handleMarkResolved = async (todoId, currentResolvedStatus) => {
    try {
      await updateMetadata({
        variables: {
          user_id: user_id,
          todo_id: todoId,
          metadata: {
            customerResolved: !currentResolvedStatus
          }
        }
      });
      refetch();
    } catch (error) {
      // Silently handle error - refetch will show current state
    }
  };

  return (
    <>
      {/* Only show contact form when NOT showing resolved items */}
      {!showResolved && (
        <form onSubmit={handleSubmit}>
          <PageTitle titleText="Contact Us" />
          <FormInput label="Message" name="message" error={errors.message} multiline rows={4} />
          <PurpleButton
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </PurpleButton>
          <Box mt={2}>
            <StatusMessage status={status} />
          </Box>
        </form>
      )}

      {/* Page title for resolved items page */}
      {showResolved && <PageTitle titleText="Resolved Conversations" />}

      {/* Contact Submissions Section */}
      {!loading && data?.getTodosAndCategories?.todos?.filter(todo =>
        todo.isAdminContactTodo === true
      ).length > 0 && (
        <Box mt={showResolved ? 0 : 4}>
          {!showResolved && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Conversations
              </Typography>
            </>
          )}

          {/* Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search conversations..."
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
          />

          {contactTodos.length === 0 && searchString.length >= 2 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              No conversations found matching "{searchString}"
            </Typography>
          ) : (
            contactTodos.map((todo) => {
            const isResolved = todo.contactMetadata?.customerResolved;
            const submittedDate = todo.contactMetadata?.submittedDate;
            const messageCount = todo.conversation?.length || 0;

            // Determine who sent the original message (first conversation message)
            const originalMessage = todo.conversation?.[0];
            const isFromCurrentUser = hashedUserId && originalMessage?.authorUserId === hashedUserId;
            const fromLabel = isFromCurrentUser ? 'You' : originalMessage?.authorName || 'Unknown';

            return (
              <Accordion
                key={todo.id}
                expanded={expandedPanel === todo.id}
                onChange={handleAccordionChange(todo.id)}
                sx={{
                  mb: 2,
                  boxShadow: expandedPanel === todo.id
                    ? '0px 10px 30px rgba(0,0,0,0.2)'
                    : '0px 3px 8px rgba(0,0,0,0.12)',
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: expandedPanel === todo.id
                      ? '0px 10px 30px rgba(0,0,0,0.2)'
                      : '0px 4px 12px rgba(0,0,0,0.15)',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'grey.50',
                    '&:hover': { backgroundColor: 'grey.100' }
                  }}
                >
                  <Box sx={{ width: '100%', pr: 2 }}>
                    {/* From Label */}
                    <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      From: {fromLabel}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {todo.description}
                      </Typography>
                      {isResolved ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Resolved"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<PendingIcon />}
                          label="Pending"
                          size="small"
                          color="warning"
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Submitted: {formatDate(submittedDate)}
                      </Typography>
                      {messageCount > 0 && (
                        <Chip
                          icon={<MessageIcon />}
                          label={`${messageCount} message${messageCount > 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box sx={{ width: '100%' }}>
                    {/* Conversation */}
                    {messageCount > 0 ? (
                      <Box>
                        <ConversationView
                          todoId={todo.id}
                          conversation={todo.conversation}
                          responders={todo.responders}
                          onMessageAdded={handleConversationUpdate}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No responses yet. We'll reply to your message soon.
                      </Typography>
                    )}

                    {/* Status Info */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Status: {isResolved ? 'This issue has been marked as resolved' : 'Waiting for response'}
                        </Typography>
                        {todo.contactMetadata?.lastViewedByAdmin && (
                          <Typography variant="caption" color="text.secondary">
                            Last viewed by admin: {formatDate(todo.contactMetadata.lastViewedByAdmin)}
                          </Typography>
                        )}
                      </Box>

                      {/* Mark Resolved Button */}
                      <Box>
                        <PurpleButton
                          variant={isResolved ? "outlined" : "contained"}
                          size="small"
                          onClick={() => handleMarkResolved(todo.id, isResolved)}
                          startIcon={isResolved ? <PendingIcon /> : <CheckCircleIcon />}
                        >
                          {isResolved ? 'Reopen Issue' : 'Mark as Resolved'}
                        </PurpleButton>
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })
          )}
        </Box>
      )}
    </>
  );
};

const EmailForms = (user) => {
  return (
    <Card>
      <CardHeader />
      <CardContent>
        <ContactForm {...user}/>
      </CardContent>
    </Card>
  );
};

export default EmailForms;